import React from 'react';

const ConversationList = ({ 
  conversations = [], 
  selectedConversation, 
  onSelectConversation,
  currentUserType = 'client',
  loading = false 
}) => {
  if (loading) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: '#666'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #007bff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 1rem'
        }}></div>
        Loading conversations...
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div style={{
        padding: '3rem 2rem',
        textAlign: 'center',
        color: '#666'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💬</div>
        <h3 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>No conversations yet</h3>
        <p style={{ margin: 0, fontSize: '0.9rem' }}>
          {currentUserType === 'client' 
            ? 'Submit an estimate to start chatting with our team!'
            : 'Conversations will appear here when clients submit estimates.'
          }
        </p>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', overflowY: 'auto' }}>
      {conversations.map((conversation) => {
        const isSelected = selectedConversation?.id === conversation.id;
        const hasUnread = currentUserType === 'admin' ? 
          conversation.hasUnreadMessages : 
          conversation.messages?.some(msg => msg.sender === 'admin' && !msg.read);
        
        const lastMessage = conversation.messages && conversation.messages.length > 0 
          ? conversation.messages[conversation.messages.length - 1]
          : null;

        return (
          <div
            key={conversation.id}
            onClick={() => onSelectConversation(conversation)}
            style={{
              padding: '1rem 1.25rem',
              borderBottom: '1px solid #f0f0f0',
              cursor: 'pointer',
              backgroundColor: isSelected ? '#e3f2fd' : 'white',
              borderLeft: isSelected ? '4px solid #007bff' : '4px solid transparent',
              transition: 'all 0.2s ease',
              position: 'relative'
            }}
            onMouseOver={(e) => {
              if (!isSelected) {
                e.target.style.backgroundColor = '#f8f9fa';
              }
            }}
            onMouseOut={(e) => {
              if (!isSelected) {
                e.target.style.backgroundColor = 'white';
              }
            }}
          >
            {/* Unread indicator */}
            {hasUnread && (
              <div style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                width: '10px',
                height: '10px',
                backgroundColor: '#dc3545',
                borderRadius: '50%',
                border: '2px solid white'
              }}></div>
            )}

            {/* Conversation Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '0.5rem'
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h4 style={{
                  margin: '0 0 0.25rem 0',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  color: '#333',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {currentUserType === 'admin' 
                    ? (conversation.userName || conversation.userEmail?.split('@')[0] || 'Client')
                    : conversation.service || 'Tree Service Request'
                  }
                </h4>
                
                <div style={{
                  fontSize: '0.8rem',
                  color: '#666',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{
                    background: getStatusColor(conversation.status),
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    fontSize: '0.7rem',
                    fontWeight: '500'
                  }}>
                    {conversation.status?.toUpperCase() || 'PENDING'}
                  </span>
                  
                  {conversation.quoteAmount && (
                    <span style={{ color: '#28a745', fontWeight: '600' }}>
                      ${conversation.quoteAmount.toLocaleString()}
                    </span>
                  )}
                </div>
              </div>

              <div style={{
                fontSize: '0.75rem',
                color: '#999',
                textAlign: 'right',
                marginLeft: '0.5rem'
              }}>
                {conversation.updatedAt ? 
                  formatRelativeTime(conversation.updatedAt) :
                  formatRelativeTime(conversation.submittedDate)
                }
              </div>
            </div>

            {/* Last Message Preview */}
            {lastMessage && (
              <div style={{
                fontSize: '0.85rem',
                color: '#666',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                marginTop: '0.5rem'
              }}>
                <span style={{ 
                  fontWeight: '500',
                  color: lastMessage.sender === currentUserType ? '#007bff' : '#666'
                }}>
                  {lastMessage.sender === currentUserType ? 'You: ' : 
                   lastMessage.sender === 'admin' ? 'Admin: ' :
                   lastMessage.sender === 'system' ? 'System: ' : 'Client: '}
                </span>
                {lastMessage.isQuote ? 
                  `💰 Quote: $${lastMessage.quoteAmount?.toLocaleString()}` :
                  lastMessage.isSignatureRequest ? 
                    '✍️ Signature request sent' :
                    lastMessage.text
                }
              </div>
            )}

            {/* Message Count */}
            {conversation.messages && conversation.messages.length > 0 && (
              <div style={{
                fontSize: '0.75rem',
                color: '#999',
                marginTop: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}>
                💬 {conversation.messages.length} message{conversation.messages.length !== 1 ? 's' : ''}
                {hasUnread && (
                  <span style={{
                    background: '#dc3545',
                    color: 'white',
                    padding: '1px 6px',
                    borderRadius: '8px',
                    fontSize: '0.7rem',
                    fontWeight: '600',
                    marginLeft: '0.5rem'
                  }}>
                    NEW
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Helper function to get status color
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'pending': return '#ffc107';
    case 'quoted': return '#17a2b8';
    case 'approved': return '#28a745';
    case 'declined': return '#dc3545';
    case 'completed': return '#6f42c1';
    default: return '#6c757d';
  }
};

// Helper function to format relative time
const formatRelativeTime = (date) => {
  if (!date) return '';
  
  const now = new Date();
  const messageDate = date.toDate ? date.toDate() : new Date(date);
  const diffInSeconds = Math.floor((now - messageDate) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  
  return messageDate.toLocaleDateString();
};

export default ConversationList;

